import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { useReactivationProjection, useDealsSourceChannel } from '@/hooks/useReactivationProjection';
import { RefreshCw, TrendingUp, Calendar, Users, DollarSign, Target, Loader2, ChevronLeft } from 'lucide-react';
import { format, parseISO, startOfISOWeek, endOfISOWeek, eachDayOfInterval, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(142, 76%, 36%)',
  'hsl(262, 83%, 58%)',
];

// Portuguese day abbreviations
const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export default function ChannelsOriginMetrics({ deals, users }) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week' | 'day'
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  
  const { data: reactivationData, isLoading: reactivationLoading, refetch } = useReactivationProjection();
  const { sourceData, channelData, reactivationDeals, totalDeals } = useDealsSourceChannel(deals);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatMonthLabel = (dateStr) => {
    try {
      const date = parseISO(dateStr + '-01');
      return format(date, 'MMM yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDayLabel = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'dd/MM');
    } catch {
      return dateStr;
    }
  };

  // Filter weekly data for selected month
  const filteredWeeklyData = useMemo(() => {
    if (!selectedMonth || !reactivationData?.weekly) return reactivationData?.weekly || [];
    return reactivationData.weekly.filter(w => w.startDate.startsWith(selectedMonth) || w.endDate.startsWith(selectedMonth));
  }, [selectedMonth, reactivationData?.weekly]);

  // Generate all 7 days for selected week (Mon-Sun), always showing Sat/Sun
  const weekDaysData = useMemo(() => {
    if (!selectedWeek || !reactivationData?.daily) return [];
    
    const weekData = reactivationData.weekly?.find(w => w.weekKey === selectedWeek);
    if (!weekData) return [];
    
    const weekStart = parseISO(weekData.startDate);
    const weekEnd = parseISO(weekData.endDate);
    const allDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
    
    // Create map of daily data
    const dailyMap = new Map(reactivationData.daily.map(d => [d.date, d.count]));
    
    return allDays.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayOfWeek = getDay(date);
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      return {
        date: dateStr,
        count: dailyMap.get(dateStr) || 0,
        isWeekend,
        dayLabel: `${DAY_LABELS[dayOfWeek]} ${format(date, 'dd')}`,
      };
    });
  }, [selectedWeek, reactivationData?.daily, reactivationData?.weekly]);

  // Filter daily data for selected month (when viewing from month directly)
  const filteredDailyData = selectedMonth && reactivationData?.daily
    ? reactivationData.daily.filter(d => d.date.startsWith(selectedMonth))
    : reactivationData?.daily || [];

  const chartData = useMemo(() => {
    if (viewMode === 'month') return reactivationData?.monthly || [];
    if (viewMode === 'week') return filteredWeeklyData;
    if (viewMode === 'day' && selectedWeek) return weekDaysData;
    return filteredDailyData;
  }, [viewMode, reactivationData?.monthly, filteredWeeklyData, weekDaysData, filteredDailyData, selectedWeek]);

  const handleBarClick = (data) => {
    if (viewMode === 'month') {
      setSelectedMonth(data.date);
      setViewMode('week');
    } else if (viewMode === 'week') {
      setSelectedWeek(data.weekKey);
      setViewMode('day');
    }
  };

  const handleBack = () => {
    if (viewMode === 'day') {
      setSelectedWeek(null);
      setViewMode('week');
    } else if (viewMode === 'week') {
      setSelectedMonth(null);
      setViewMode('month');
    }
  };

  return (
    <div className="space-y-6">
      {/* Source & Channel Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Source */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              {t('channelsOrigin.bySource', 'Deals por Origem')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Header */}
            <div className="grid grid-cols-[1fr_70px_70px_100px] gap-2 pb-2 mb-2 border-b text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span>Origem</span>
              <span className="text-center">Deals</span>
              <span className="text-center">Conv.</span>
              <span className="text-right">Valor</span>
            </div>
            
            {/* Data Rows */}
            <div className="space-y-1">
              {sourceData.slice(0, 8).map((source, idx) => (
                <div 
                  key={source.name} 
                  className="grid grid-cols-[1fr_70px_70px_100px] gap-2 items-center py-1.5 hover:bg-muted/50 rounded-md px-1 -mx-1 transition-colors"
                >
                  {/* Nome com cor */}
                  <div className="flex items-center gap-2 min-w-0">
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <span className="text-sm font-medium truncate">{source.name}</span>
                  </div>
                  
                  {/* Deals */}
                  <span className="text-sm text-center font-semibold tabular-nums">
                    {source.total.toLocaleString()}
                  </span>
                  
                  {/* Conversão colorida */}
                  <span className={`text-sm text-center font-medium tabular-nums ${
                    source.conversionRate >= 50 ? 'text-green-600 dark:text-green-500' :
                    source.conversionRate >= 20 ? 'text-yellow-600 dark:text-yellow-500' :
                    'text-muted-foreground'
                  }`}>
                    {source.conversionRate.toFixed(0)}%
                  </span>
                  
                  {/* Valor */}
                  <span className="text-sm text-right font-semibold tabular-nums">
                    {formatCurrency(source.totalValue)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Channel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              {t('channelsOrigin.byChannel', 'Deals por Canal')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={channelData.slice(0, 6)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'total' ? 'Total' : name]}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]}>
                  {channelData.slice(0, 6).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Reactivation Projection Section */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              {t('channelsOrigin.reactivationForecast', 'Projeção de Reativação')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {reactivationLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Calendar className="h-4 w-4" />
                    {t('channelsOrigin.scheduled', 'Agendados')}
                  </div>
                  <p className="text-2xl font-bold mt-1">
                    {reactivationData?.totalScheduled || 0}
                  </p>
                </div>
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Users className="h-4 w-4" />
                    {t('reactivation.inProgress', 'Em Progresso')}
                  </div>
                  <p className="text-2xl font-bold mt-1">
                    {reactivationData?.byStatus.in_progress || 0}
                  </p>
                </div>
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <TrendingUp className="h-4 w-4" />
                    {t('reactivation.completed', 'Completados')}
                  </div>
                  <p className="text-2xl font-bold mt-1 text-green-600">
                    {reactivationData?.byStatus.completed || 0}
                  </p>
                </div>
                <div className="bg-card rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <DollarSign className="h-4 w-4" />
                    {t('channelsOrigin.reactivationDeals', 'Deals Reativação')}
                  </div>
                  <p className="text-2xl font-bold mt-1 text-primary">
                    {reactivationDeals.length}
                  </p>
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {viewMode !== 'month' && (
                    <Button variant="ghost" size="sm" onClick={handleBack}>
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Voltar
                    </Button>
                  )}
                  <Tabs value={viewMode} onValueChange={(v) => {
                    setViewMode(v);
                    if (v === 'month') {
                      setSelectedMonth(null);
                      setSelectedWeek(null);
                    } else if (v === 'week') {
                      setSelectedWeek(null);
                    }
                  }}>
                    <TabsList>
                      <TabsTrigger value="month">
                        {t('channelsOrigin.viewByMonth', 'Por Mês')}
                      </TabsTrigger>
                      <TabsTrigger value="week">
                        {t('channelsOrigin.viewByWeek', 'Por Semana')}
                      </TabsTrigger>
                      <TabsTrigger value="day">
                        {t('channelsOrigin.viewByDay', 'Por Dia')}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* Context indicator */}
                <div className="flex items-center gap-2">
                  {selectedMonth && (
                    <Badge variant="outline">
                      {formatMonthLabel(selectedMonth)}
                    </Badge>
                  )}
                  {selectedWeek && (
                    <Badge variant="outline">
                      {reactivationData?.weekly?.find(w => w.weekKey === selectedWeek)?.weekLabel || selectedWeek}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Chart */}
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey={viewMode === 'week' ? 'weekLabel' : (viewMode === 'day' && selectedWeek ? 'dayLabel' : 'date')}
                      tickFormatter={viewMode === 'month' ? formatMonthLabel : (viewMode === 'week' ? (v) => v.split(' - ')[0] : (viewMode === 'day' && selectedWeek ? (v) => v : formatDayLabel))}
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={viewMode === 'week' ? -45 : 0}
                      textAnchor={viewMode === 'week' ? 'end' : 'middle'}
                      height={viewMode === 'week' ? 60 : 30}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => {
                        if (viewMode === 'month') return formatMonthLabel(value);
                        if (viewMode === 'week') return value;
                        return value;
                      }}
                      formatter={(value) => [value, t('channelsOrigin.projectedContacts', 'Contatos')]}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      radius={[4, 4, 0, 0]}
                      onClick={handleBarClick}
                      style={{ cursor: viewMode !== 'day' ? 'pointer' : 'default' }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.isWeekend ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))'}
                          opacity={entry.isWeekend ? 0.4 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  {t('channelsOrigin.noReactivationsScheduled', 'Nenhuma reativação agendada')}
                </div>
              )}

              {/* Legend for day view */}
              {viewMode === 'day' && selectedWeek && (
                <div className="flex items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'hsl(var(--primary))' }} />
                    <span>Dias úteis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded opacity-40" style={{ backgroundColor: 'hsl(var(--muted-foreground))' }} />
                    <span>Fim de semana</span>
                  </div>
                </div>
              )}

              {/* Weekly Summary Grid */}
              {viewMode === 'week' && filteredWeeklyData.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">
                    {t('channelsOrigin.weeklyBreakdown', 'Detalhamento Semanal')}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {filteredWeeklyData.map(w => (
                      <button
                        key={w.weekKey}
                        className="bg-card rounded-lg p-3 border hover:border-primary transition-colors text-left"
                        onClick={() => {
                          setSelectedWeek(w.weekKey);
                          setViewMode('day');
                        }}
                      >
                        <p className="text-xs text-muted-foreground">{w.weekLabel}</p>
                        <p className="text-lg font-bold">{w.count}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Summary Table */}
              {viewMode === 'month' && reactivationData?.monthly.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">
                    {t('channelsOrigin.monthlyBreakdown', 'Detalhamento Mensal')}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {reactivationData.monthly.map(m => (
                      <button
                        key={m.date}
                        className="bg-card rounded-lg p-3 border hover:border-primary transition-colors text-left"
                        onClick={() => {
                          setSelectedMonth(m.date);
                          setViewMode('week');
                        }}
                      >
                        <p className="text-xs text-muted-foreground">{formatMonthLabel(m.date)}</p>
                        <p className="text-lg font-bold">{m.count}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
